"use client";

import { useEffect, useRef } from "react";
import AppImage from "@/components/ui/AppImage";
import Icon from "@/components/ui/AppIcon";

const testimonials = [
{
  quote:
  "I\'ve been cycling for 12 years and VelocityCycles gave me the best buying experience I\'ve ever had. The Apex Road Racer handles like a dream on the Marin County trails.",
  name: "Marcus Delgado",
  role: "Competitive Cyclist, San Francisco CA",
  avatar: "https://images.unsplash.com/photo-1723710428423-5717e63a4ae0",
  avatarAlt: "Marcus Delgado, a man with short brown hair smiling outdoors",
  stars: 5,
  border: "testimonial-border-amber"
},
{
  quote:
  "Switched from a big-box store to VelocityCycles for my daughter\'s first mountain bike. The staff spent 45 minutes fitting her properly. She hasn\'t stopped riding since.",
  name: "Priya Nair",
  role: "Parent & Recreational Rider, Portland OR",
  avatar: "https://images.unsplash.com/photo-1656897497082-56366c11b727",
  avatarAlt: "Priya Nair, a woman with dark hair wearing a casual top",
  stars: 5,
  border: "testimonial-border-charcoal"
}];


export default function TestimonialsSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.remove("hidden");
        });
      },
      { threshold: 0.1 }
    );
    const reveals = sectionRef?.current?.querySelectorAll(".reveal");
    reveals?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="reviews" ref={sectionRef} className="py-28 px-6 bg-cream">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-20 items-center">
          {/* Left: Quotes */}
          <div className="space-y-10 reveal hidden">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.3em] text-amber mb-4">
                Rider Stories
              </p>
              <h2 className="font-heading text-5xl md:text-6xl font-black leading-none tracking-tight text-charcoal">
                Trusted by
                <br />
                <span className="italic font-light">Real Cyclists.</span>
              </h2>
            </div>

            {testimonials?.map((t) =>
            <div key={t?.name} className={`${t?.border} pl-6 space-y-4`}>
                {/* Stars */}
                <div className="flex gap-1">
                  {Array.from({ length: t?.stars })?.map((_, i) =>
                <Icon key={i} name="StarIcon" size={14} className="text-amber" variant="solid" />
                )}
                </div>
                <p className="text-lg font-medium italic leading-relaxed text-charcoal">
                  "{t?.quote}"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-stone-border">
                    <AppImage
                    src={t?.avatar}
                    alt={t?.avatarAlt}
                    width={40}
                    height={40}
                    className="object-cover w-full h-full" />
                  
                  </div>
                  <div>
                    <p className="font-semibold text-charcoal text-sm">{t?.name}</p>
                    <p className="text-stone-warm text-xs uppercase tracking-widest">{t?.role}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right: Image */}
          <div className="relative reveal hidden reveal-delay-2">
            <div className="rounded-5xl overflow-hidden shadow-card-hover aspect-[4/5]">
              <AppImage
                src="https://images.unsplash.com/photo-1541995602-11551866c315"
                alt="Cyclist riding a mountain bike on a scenic forest trail in autumn"
                fill
                className="object-cover" />
              
            </div>

            {/* Floating award badge */}
            <div className="absolute -top-6 -right-4 w-32 h-32 bg-amber rounded-full flex flex-col items-center justify-center text-charcoal p-3 text-center shadow-amber-glow"
            style={{ transform: "rotate(-12deg)" }}>
              
              <Icon name="TrophyIcon" size={24} className="text-charcoal mb-1" variant="solid" />
              <p className="text-[9px] font-black uppercase tracking-widest leading-tight">Best Bike Shop 2025</p>
              <p className="text-[8px] font-bold mt-0.5">Bay Area</p>
            </div>

            {/* Floating rating card */}
            <div className="absolute -bottom-6 -left-6 glass-card rounded-2xl p-4 shadow-card">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber rounded-xl flex items-center justify-center">
                  <Icon name="StarIcon" size={18} className="text-charcoal" variant="solid" />
                </div>
                <div>
                  <p className="font-heading text-xl font-black text-charcoal">4.9 / 5.0</p>
                  <p className="text-xs text-stone-warm font-medium">1,247 verified reviews</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

}